import { useAuth } from "@/lib/AuthContext";
import Sidebar from "@/components/layout/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { List, Question, BookOpen, MagnifyingGlass, Folders, ListChecks, ChatCircleDots, GitPullRequest, ClockCounterClockwise, TestTube, Database } from "@phosphor-icons/react";
import StaleVersionBanner from "@/components/StaleVersionBanner";
import RouteErrorBoundary from "@/components/system/RouteErrorBoundary";
import HealthIndicator from "@/components/system/HealthIndicator";

const TYPE_ICONS = { project: Folders, task: ListChecks, change_request: GitPullRequest, message: ChatCircleDots };
const TYPE_COLORS = { project: "text-[#0055FF]", task: "text-[#16A34A]", change_request: "text-[#D97706]", message: "text-[#7C3AED]" };

export default function AppLayout({ children }) {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const helpRef = useRef(null);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (helpRef.current && !helpRef.current.contains(e.target)) setHelpOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchResults(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (val) => {
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val.trim()) { setSearchResults(null); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await api.get(`/search?q=${encodeURIComponent(val.trim())}`);
        setSearchResults(res.data);
      } catch { setSearchResults({ results: [], count: 0 }); }
      finally { setSearchLoading(false); }
    }, 300);
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: "var(--theme-page-bg, #FFFFFF)", color: "var(--theme-page-fg, #0A0A0A)" }} data-testid="app-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`fixed md:static z-50 h-full transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Stale-bundle banner — appears when the user's compiled bundle
            is older than the currently-deployed server version. Tells
            them exactly which hard-refresh shortcut to press. */}
        <StaleVersionBanner />
        {/* Top Bar */}
        <div className="h-12 border-b border-[#E5E5E5] flex items-center justify-between px-4 bg-white sticky top-0 z-30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1.5" data-testid="mobile-menu-button">
              <List size={20} weight="bold" className="text-[#0A0A0A]" />
            </button>
            <span className="md:hidden text-lg font-black tracking-tighter text-[#1a2b6d]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              EYVO PULSE
            </span>
          </div>

          {/* Global Search */}
          <div className="hidden md:block relative flex-1 max-w-md mx-4" ref={searchRef}>
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
            <input
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search projects, tasks, messages..."
              className="w-full h-8 pl-9 pr-3 text-sm border border-[#E5E5E5] bg-[#FAFAFA] focus:bg-white focus:border-[#0055FF] outline-none transition-colors"
              data-testid="global-search-input"
            />
            {searchResults && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E5E5] shadow-lg max-h-80 overflow-y-auto z-50" data-testid="global-search-results">
                {searchLoading ? (
                  <div className="p-4 text-center text-xs text-[#A3A3A3]">Searching...</div>
                ) : searchResults.count === 0 ? (
                  <div className="p-4 text-center text-xs text-[#A3A3A3]">No results found</div>
                ) : (
                  searchResults.results.slice(0, 15).map((r, i) => {
                    const Icon = TYPE_ICONS[r.type] || Folders;
                    const color = TYPE_COLORS[r.type] || "text-[#525252]";
                    return (
                      <button key={`${r.type}-${r.id}-${i}`} onClick={() => { navigate(r.link); setSearchResults(null); setSearchQuery(""); }}
                        className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-[#F5F5F5] transition-colors text-left border-b border-[#F5F5F5] last:border-0"
                        data-testid={`search-result-${r.type}-${i}`}
                      >
                        <Icon size={16} className={`${color} flex-shrink-0 mt-0.5`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0A0A0A] truncate">{r.title}</p>
                          <p className="text-[10px] text-[#A3A3A3] mt-0.5">{r.type.replace("_", " ")} &middot; {r.subtitle}</p>
                        </div>
                      </button>
                    );
                  })
                )}
                {searchResults.count > 15 && (
                  <div className="px-4 py-2 text-[10px] text-[#A3A3A3] text-center border-t border-[#E5E5E5]">
                    Showing 15 of {searchResults.count} results
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative" ref={helpRef}>
              <button onClick={() => setHelpOpen(!helpOpen)} className="p-1.5 rounded-sm hover:bg-[#F5F5F5] transition-colors text-[#525252] hover:text-[#0055FF]" data-testid="help-menu-button">
                <Question size={20} weight="bold" />
              </button>
              {helpOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-[#E5E5E5] shadow-lg z-50" data-testid="help-menu-dropdown">
                  {user?.user_type === "internal" && (
                    <button onClick={() => { navigate("/guide/internal"); setHelpOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors text-left" data-testid="help-internal-guide">
                      <BookOpen size={16} className="text-[#0055FF] flex-shrink-0" /> Internal User Guide
                    </button>
                  )}
                  <button onClick={() => { navigate("/guide/client"); setHelpOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors text-left" data-testid="help-client-guide">
                    <BookOpen size={16} className="text-[#7C3AED] flex-shrink-0" /> Client User Guide
                  </button>
                  {user?.user_type === "internal" && (
                    <button onClick={() => { navigate("/guide/testing"); setHelpOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors text-left border-t border-[#F5F5F5]" data-testid="help-testing-plan">
                      <TestTube size={16} className="text-[#16A34A] flex-shrink-0" /> Testing Plan
                    </button>
                  )}
                  {hasRole("platform_admin") && (
                    <button onClick={() => { navigate("/revision-history"); setHelpOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors text-left border-t border-[#F5F5F5]" data-testid="help-revision-history">
                      <ClockCounterClockwise size={16} className="text-[#525252] flex-shrink-0" /> Revision History
                    </button>
                  )}
                  {hasRole("platform_admin") && (
                    <button onClick={() => { navigate("/admin/db-connections"); setHelpOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors text-left" data-testid="help-db-connections">
                      <Database size={16} className="text-[#0055FF] flex-shrink-0" weight="duotone" /> Database Connections
                    </button>
                  )}
                </div>
              )}
            </div>
            {user?.user_type === "internal" && <HealthIndicator />}
            <NotificationBell />
          </div>
        </div>

        <main className="flex-1 overflow-y-auto" style={{ backgroundColor: "var(--theme-page-bg, #FFFFFF)", color: "var(--theme-page-fg, #0A0A0A)" }}>
          {/* Keyed by path: a page that crashes shows an error card while
              the shell keeps working, and navigating away resets it. */}
          <RouteErrorBoundary key={location.pathname}>{children}</RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
